import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { FormControl } from "react-bootstrap";
import {
  LuAlignCenter,
  LuAlignLeft,
  LuAlignRight,
  LuAlignVerticalJustifyCenter,
  LuAlignVerticalJustifyEnd,
  LuAlignVerticalJustifyStart,
  LuBold,
  LuItalic,
} from "react-icons/lu";
import { SUPPORTED_GOOGLE_FONTS } from "../../../common/font_extractor.es6";
import {
  ColorField,
  Field,
  IconToggle,
  Section,
  Segmented,
  SelectField,
  SliderRow,
} from "./controls";

export const t = (key, fallback) => i18n.t(`ShapeSettings.${key}`, { defaultValue: fallback });

// Each section body gets: state (resolved), shape (catalog entry) and
// update(key, patch|value).
const tabPropTypes = {
  state: PropTypes.object.isRequired,
  shape: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
};

export const FillTab = ({ state, update }) => {
  const { fill } = state;
  return (
    <Section>
      <Segmented
        testId="fill-type"
        value={fill.type}
        onChange={(type) => update("fill", { type })}
        options={[
          { value: "none", label: t("fill_none", "Dolgu yok") },
          { value: "solid", label: t("fill_solid", "Düz renk") },
          { value: "gradient", label: t("fill_gradient", "Renk geçişi") },
        ]}
      />
      {fill.type !== "none" ? (
        <ColorField
          testId="fill-color"
          label={t("main_color", "Ana renk")}
          value={fill.color}
          onChange={(color) => update("fill", { color })}
        />
      ) : null}
      {fill.type === "gradient" ? (
        <>
          <ColorField
            testId="fill-color2"
            label={t("second_color", "İkinci renk")}
            value={fill.color2}
            onChange={(color2) => update("fill", { color2 })}
          />
          <SliderRow
            label={t("angle", "Açı")}
            value={Number(fill.angle) || 0}
            min={0}
            max={360}
            unit="°"
            onChange={(angle) => update("fill", { angle })}
          />
        </>
      ) : null}
    </Section>
  );
};
FillTab.propTypes = tabPropTypes;

export const StrokeColorTab = ({ state, update }) => {
  const { stroke } = state;
  // Picking a colour for an invisible (0 px) outline gives it a width.
  const withWidth = (patch) => (Number(stroke.width) > 0 ? patch : { ...patch, width: 2 });
  return (
    <Section>
      <ColorField
        testId="stroke-color"
        label={t("color", "Renk")}
        value={stroke.color}
        onChange={(color) => update("stroke", withWidth({ color }))}
      />
    </Section>
  );
};
StrokeColorTab.propTypes = tabPropTypes;

const CAP_OPTIONS = () => [
  { value: "none", label: t("cap_none", "Yok") },
  { value: "arrow", label: t("cap_arrow", "Ok") },
  { value: "circle", label: t("cap_circle", "Daire") },
];

export const StrokeStyleTab = ({ state, shape, update }) => {
  const { stroke } = state;
  const isLine = shape.kind === "line";
  return (
    <Section>
      <SliderRow
        testId="stroke-width"
        label={t("width", "Kalınlık")}
        value={Number(stroke.width) || 0}
        min={0}
        max={20}
        unit="px"
        onChange={(width) => update("stroke", { width })}
      />
      <Field label={t("dash", "Tür")}>
        <Segmented
          testId="stroke-dash"
          value={stroke.dash}
          onChange={(dash) => update("stroke", { dash })}
          options={[
            { value: "solid", label: t("dash_solid", "Düz") },
            { value: "dashed", label: t("dash_dashed", "Kesik") },
            { value: "dotted", label: t("dash_dotted", "Noktalı") },
          ]}
        />
      </Field>
      {isLine ? (
        <>
          <Field label={t("start_cap", "Başlangıç ucu")}>
            <Segmented
              value={stroke.startCap}
              onChange={(startCap) => update("stroke", { startCap })}
              options={CAP_OPTIONS()}
            />
          </Field>
          <Field label={t("end_cap", "Bitiş ucu")}>
            <Segmented
              value={stroke.endCap}
              onChange={(endCap) => update("stroke", { endCap })}
              options={CAP_OPTIONS()}
            />
          </Field>
          <Field label={t("line_cap", "Uç şekli")}>
            <Segmented
              value={stroke.lineCap}
              onChange={(lineCap) => update("stroke", { lineCap })}
              options={[
                { value: "butt", label: t("line_cap_butt", "Düz") },
                { value: "round", label: t("line_cap_round", "Yuvarlak") },
              ]}
            />
          </Field>
        </>
      ) : null}
    </Section>
  );
};
StrokeStyleTab.propTypes = tabPropTypes;

const RADIUS_PRESETS = [0, 8, 16, 24];

export const RadiusTab = ({ state, shape, update }) => {
  const own = state.radius == null ? shape.radius : state.radius;
  const isPill = own === "pill";
  const numeric = typeof own === "number" ? own : 0;
  return (
    <Section>
      <SliderRow
        testId="radius"
        label={t("radius", "Yarıçap")}
        value={isPill ? 100 : Math.min(100, numeric)}
        min={0}
        max={100}
        unit="px"
        onChange={(r) => update("radius", r)}
      />
      <Segmented
        testId="radius-presets"
        value={isPill ? "pill" : numeric}
        onChange={(r) => update("radius", r)}
        options={[
          ...RADIUS_PRESETS.map((r) => ({ value: r, label: String(r) })),
          { value: "pill", label: t("radius_full", "Tam") },
        ]}
      />
    </Section>
  );
};
RadiusTab.propTypes = tabPropTypes;

const SHADOW_PRESETS = {
  soft: { x: 0, y: 2, blur: 6, color: "rgba(15,23,42,0.15)" },
  medium: { x: 0, y: 4, blur: 12, color: "rgba(15,23,42,0.25)" },
  strong: { x: 0, y: 10, blur: 24, color: "rgba(15,23,42,0.35)" },
};

const shadowPresetOf = (shadow) => {
  if (!shadow.enabled) return "none";
  const hit = Object.keys(SHADOW_PRESETS).find((k) => {
    const p = SHADOW_PRESETS[k];
    return p.x === Number(shadow.x) && p.y === Number(shadow.y) && p.blur === Number(shadow.blur);
  });
  return hit || null;
};

export const ShadowTab = ({ state, update }) => {
  const { shadow } = state;
  return (
    <Section>
      <Segmented
        testId="shadow-presets"
        value={shadowPresetOf(shadow)}
        onChange={(key) =>
          update(
            "shadow",
            key === "none" ? { enabled: false } : { ...SHADOW_PRESETS[key], enabled: true }
          )
        }
        options={[
          { value: "none", label: t("shadow_none", "Yok") },
          { value: "soft", label: t("shadow_soft", "Hafif") },
          { value: "medium", label: t("shadow_medium", "Orta") },
          { value: "strong", label: t("shadow_strong", "Belirgin") },
        ]}
      />
      {shadow.enabled ? (
        <>
          <SliderRow
            label="X"
            value={Number(shadow.x) || 0}
            min={-30}
            max={30}
            unit="px"
            onChange={(x) => update("shadow", { x })}
          />
          <SliderRow
            label="Y"
            value={Number(shadow.y) || 0}
            min={-30}
            max={30}
            unit="px"
            onChange={(y) => update("shadow", { y })}
          />
          <SliderRow
            label={t("blur", "Bulanıklık")}
            value={Number(shadow.blur) || 0}
            min={0}
            max={60}
            unit="px"
            onChange={(blur) => update("shadow", { blur })}
          />
          <ColorField
            label={t("color", "Renk")}
            value={shadow.color}
            onChange={(color) => update("shadow", { color })}
          />
        </>
      ) : null}
    </Section>
  );
};
ShadowTab.propTypes = tabPropTypes;

export const TextTab = ({ state, update }) => {
  const { text } = state;
  return (
    <Section>
      <FormControl
        as="textarea"
        className="shape-settings__textarea"
        data-testid="shape-text"
        rows={3}
        placeholder={t("text_placeholder", "Şeklin içine yazı yazın…")}
        value={text.value}
        onChange={(e) => update("text", { value: e.target.value })}
      />
      <SelectField
        label={t("font", "Yazı tipi")}
        value={text.font}
        onChange={(font) => update("text", { font })}
      >
        <option value="">{t("font_default", "Varsayılan")}</option>
        {SUPPORTED_GOOGLE_FONTS.map((font) => (
          <option key={font} value={font}>
            {font}
          </option>
        ))}
      </SelectField>
      <ColorField
        label={t("color", "Renk")}
        value={text.color}
        onChange={(color) => update("text", { color })}
      />
      <SliderRow
        label={t("size", "Boyut")}
        value={Number(text.size) || 18}
        min={8}
        max={96}
        unit="px"
        onChange={(size) => update("text", { size })}
      />
      <Field label={t("align", "Hizalama")}>
        <div className="shape-settings__row">
          <IconToggle
            active={text.bold}
            title={t("bold", "Kalın")}
            onClick={() => update("text", { bold: !text.bold })}
          >
            <LuBold size={14} />
          </IconToggle>
          <IconToggle
            active={text.italic}
            title={t("italic", "İtalik")}
            onClick={() => update("text", { italic: !text.italic })}
          >
            <LuItalic size={14} />
          </IconToggle>
          <Segmented
            value={text.align}
            onChange={(align) => update("text", { align })}
            options={[
              { value: "left", Icon: LuAlignLeft, title: t("align_left", "Sola") },
              { value: "center", Icon: LuAlignCenter, title: t("align_center", "Ortaya") },
              { value: "right", Icon: LuAlignRight, title: t("align_right", "Sağa") },
            ]}
          />
        </div>
      </Field>
      <Field label={t("valign", "Dikey hizalama")}>
        <Segmented
          value={text.valign}
          onChange={(valign) => update("text", { valign })}
          options={[
            { value: "top", Icon: LuAlignVerticalJustifyStart, title: t("valign_top", "Üst") },
            {
              value: "middle",
              Icon: LuAlignVerticalJustifyCenter,
              title: t("valign_middle", "Orta"),
            },
            { value: "bottom", Icon: LuAlignVerticalJustifyEnd, title: t("valign_bottom", "Alt") },
          ]}
        />
      </Field>
      <SliderRow
        label={t("padding", "İç boşluk")}
        value={Number(text.padding) || 0}
        min={0}
        max={60}
        unit="px"
        onChange={(padding) => update("text", { padding })}
      />
    </Section>
  );
};
TextTab.propTypes = tabPropTypes;
